import { getLegalPage, getLETVEpisodes } from 'api/content'
import { getReferralCodeValidation } from 'api/promo'
import { useQuery } from '@tanstack/react-query'

export const useReferralCode = (referralCode: string) =>
  useQuery<any>(['referralCode', referralCode], () => getReferralCodeValidation(referralCode))

export const useFaq = (slug: string) =>
  useQuery<any>(['faq', slug], () => getLegalPage(slug), {
    select: ({ pageContent }) => pageContent.map((c: any) => ({
      question: c.title,
      answer: c.answer,
    })),
  })

function mapMarketingLeTVHostDetail(host: any): App.MarketingLeTVHostDetail {
  return {
    name: host.name,
    description: host.description,
    image: { id: String(host.imageId) },
    modalImage: { id: String(host.modalImageId) },
  }
}

function mapMarketingLeTVOfferCarousel(carousel: any): App.MarketingLeTVOfferCarousel {
  return {
    title: String(carousel.title),
    location: String(carousel.location),
    filters: {
      campaigns: carousel.filters.campaigns,
      locations: carousel.filters.locations,
      offerTypes: !carousel.filters.offerTypes?.length ? ['hotel,tactical_ao_hotel'] : carousel.filters.offerTypes,
    },
    url: carousel.url,
  }
}

function mapMarketingLeTVEpisode(episode: any): App.MarketingLeTVEpisode {
  return {
    episode: Number(episode.episode),
    heroImage: {
      id: String(episode.heroImageId),
    },
    carouselImageId: {
      id: String(episode.carouselImageId),
    },
    darkCarouselImageId: {
      id: String(episode.darkCarouselImageId),
    },
    videoId: String(episode.videoId),
    thumbnail: {
      id: String(episode.thumbnail),
    },
    isLive: Boolean(new Date() > new Date(episode.liveDate)),
    startDate: String(episode.startDate),
    endDate: String(episode.endDate),
    header: String(episode.header),
    description: episode.description ? String(episode.description) : undefined,
    episodeHosts: episode.episodeHosts?.map(mapMarketingLeTVHostDetail) ?? [],
    featuredCarousel: episode.featuredCarousel ? mapMarketingLeTVOfferCarousel(episode.featuredCarousel) : undefined,
    sponsorLogos: episode.sponsors?.map((sponsor: any) => ({
      id: sponsor,
    })) ?? [],
    exploreMoreOfferCarousels: episode.exploreMore?.map(mapMarketingLeTVOfferCarousel) ?? [],
    dreamBlockArticles: episode.dreamBlockArticles ?? undefined,
  }
}

function mapMarketingLeTVConfig(data: any, previewMode: boolean, episode: number = 0): App.MarketingLeTVConfig {
  const res = {
    leTVEpisodes: data.leTVEpisodes.map(mapMarketingLeTVEpisode),
    allHosts: data.allHosts.map(mapMarketingLeTVHostDetail) ?? [],
    destinationToExplore: data.destinationToExplore,
  }
  if (previewMode) {
    const liveEpisode = { ...res.leTVEpisodes[episode ?? 0], isLive: true }
    const updatedEpisodes = [...res.leTVEpisodes.slice(0, episode), liveEpisode, ...res.leTVEpisodes.slice(episode + 1)]
    return { ...res, leTVEpisodes: updatedEpisodes }
  }
  return res
}

export function useLETVEpisodes(previewMode = false, episode?: number) {
  return useQuery({
    queryKey: ['le-tv-episodes'],
    queryFn: () => getLETVEpisodes(),
    select: (response): App.MarketingLeTVConfig => {
      return mapMarketingLeTVConfig(response.result, previewMode, episode)
    },
  })
}
